import { FC, useMemo, useState, useRef, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  BackHeader,
  BackToTopArrow,
  Button,
  Loader,
  ActionsPopup,
  BulkActionsBar,
} from 'src/components';
import { useRouter } from 'next/router';
import { useUserContext } from 'src/context/user';
import { useExperienceContext } from 'src/context/experience';
import { ROUTES } from 'src/constants/routes.const';
import useSafePush from 'src/hooks/useSafePush';
import { getAprenderLearnSessionUrl } from 'src/utils/getAprenderLearnSessionUrl';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { WordlistsApi, PathApi, MeApi, TrackingApi, UserPathApi } from 'src/api';
import { MemriseApiError } from 'src/api/errors';
import { useIntersection } from 'src/hooks';
import { useEligibility } from 'src/hooks/useEligibility';
import { useTrackingSkillLevel } from 'src/hooks/useTrackingSkillLevel';
import { LearnablesState } from 'src/api/wordlists/types';
import { UserLanguagesResponse } from 'src/api/me/types';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { saveLastLanguagePair } from 'src/utils/language/saveLastLanguagePair';
import DashboardFrame from 'src/apps/DashboardFrame';
import { BLOB_TRACKING_DATA } from 'src/apps/LearningSession/BlobSession/tracking';
import { PlatformPage } from 'src/utils/analytics/analytics.const';
import { SelectionState } from 'src/types/selection';
import { bulkMarkAsKnown, bulkUnmarkAsKnown } from 'src/utils/bulkActions';
import { ImageBanner } from '../ImageBanner';
import { WordsAccordion } from '../WordsAccordion';
import { EmptyWordlistCard } from '../EmptyWordlistCard';
import * as S from './styles';

export const DEFAULT_PERSONAL_WORDLIST_ID = '00000000-0000-0000-0000-000000000000';

const msgs = defineMessages({
  learnedWords: { id: 'wordlists_viewlist_section1_header' },
  wordsToLearn: { id: 'wordlists_viewlist_section2_header' },
  switchCta: { id: 'wordlist_non_active_CTA' },
  chooseCta: { id: 'wordlists_viewlist_CTA' },
  upgradeCta: { id: 'onboarding_wordlist_detail_upgradeCTA' },
  startLesson: { id: 'scenario_button_start_lesson' },
  title: { id: 'wordlists_viewscreen_title' },
  placeholderTitle: { id: 'wordlists_personal_placeholder_title' },
  enrollmentHeader: { id: 'wordlist_enrollment_header' },
  enrollmentBody: { id: 'wordlist_enrollment_body' },
  enrollmentConfirm: { id: 'wordlist_enrollment_confirm' },
  enrollmentCancel: { id: 'wordlist_enrollment_cancel' },
  viewOnlyHeader: { id: 'wordlist_view_only_header' },
  viewOnlyBody: { id: 'wordlist_view_only_body' },
  viewOnlyClose: { id: 'wordlist_view_only_close' },
  switchLanguagePairHeader: { id: 'wordlist_switch_lp_header' },
  switchLanguagePairBody: { id: 'wordlist_switch_lp_body' },
  switchLanguagePairConfirm: { id: 'wordlist_switch_lp_confirm' },
  switchLanguagePairCancel: { id: 'wordlist_switch_lp_cancel' },
  addToPath: { id: 'wordlist_add_to_path_CTA', defaultMessage: 'Add to my path' },
  addToPathError: {
    id: 'wordlist_add_to_path_error',
    defaultMessage: 'This wordlist could not be added to your path. Please try again.',
  },
});

export interface WordlistWord {
  id: number;
  targetValue: string;
  sourceValue: string;
  audioUrl?: string;
  difficult?: boolean;
  known?: boolean;
  isLearned?: boolean;
  wordlistCreatorId?: number | null;
  nextReview?: string | null;
}

export const WordlistsDetailsPageComponent: FC<{
  // Optional props used when rendered in modal (e.g., onboarding flow)
  // When rendered as a page route, these come from URL query params instead
  languagePairId?: number; // languagePairIdFromModal
  wordlistId?: string; // wordlistIdFromModal
  onClose?: () => void; // Called when modal is closed (not used in page route)
  onWordlistActivated?: (wordlistId: string) => void; // Called when wordlist is activated in modal
  backRoute?: string; // Route to navigate back to (defaults to /wordlists)
}> = ({
  languagePairId: languagePairIdFromModal,
  wordlistId: wordlistIdFromModal,
  onClose,
  onWordlistActivated,
  backRoute,
}) => {
  const intl = useIntl();
  const { query, asPath } = useRouter();
  const { id: routerWordlistId, back, languagePairId: languagePairIdFromQuery } = query;

  // Prioritize URL params (for shareable links), fallback to modal props (for onboarding)
  const wordlistId = typeof routerWordlistId === 'string' ? routerWordlistId : wordlistIdFromModal;
  const languagePairId = languagePairIdFromQuery
    ? Number(languagePairIdFromQuery)
    : languagePairIdFromModal;
  const { user } = useUserContext();
  const { experienceType } = useExperienceContext();
  const { eligibility } = useEligibility(languagePairId);
  const { safePush } = useSafePush();
  const [isActive, setIsActive] = useState(false);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [showViewOnlyModal, setShowViewOnlyModal] = useState(false);
  const [showSwitchLanguagePairModal, setShowSwitchLanguagePairModal] = useState(false);
  const [hasAddToPathError, setHasAddToPathError] = useState(false);
  const isProUser = user?.isPro;
  const isOnboarding = Boolean(asPath.startsWith(ROUTES.wordlists.select));
  // Prioritize URL back param, then prop, then default to /wordlists
  const resolvedBackRoute = back ? String(back) : backRoute || ROUTES.wordlists.main;
  const is2024Experience = experienceType === '2024';
  const currentActiveLanguagePairId = getLanguagePairId();
  const isDifferentLanguagePair = useMemo(
    () =>
      languagePairId != null &&
      currentActiveLanguagePairId != null &&
      languagePairId !== currentActiveLanguagePairId,
    [languagePairId, currentActiveLanguagePairId],
  );

  useEffect(() => {
    if (wordlistId) {
      /* istanbul ignore next: analytics */
      void TrackingApi.trackUserAction(
        'screen_viewed',
        'wordlist_details_screen',
        languagePairId ?? null,
        !!isProUser,
        { wordlist_id: String(wordlistId) },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wordlistId]);
  const learnedEndOfListRef = useRef<HTMLDivElement>(null);
  const toLearnEndOfListRef = useRef<HTMLDivElement>(null);
  const accordionMiddleRef = useRef<HTMLDivElement>(null);
  const learnedEndOfListInViewport = useIntersection(learnedEndOfListRef, '10px');
  const toLearnEndOfListInViewport = useIntersection(toLearnEndOfListRef, '10px');
  const inViewportFilter = useIntersection(accordionMiddleRef, '500px');
  const emptyPersonalWordlist = wordlistId === DEFAULT_PERSONAL_WORDLIST_ID;

  const [learnedExpanded, setLearnedExpanded] = useState(false);
  const [toLearnExpanded, setToLearnExpanded] = useState(true);
  const [selectionState, setSelectionState] = useState<SelectionState>({
    selectedWordIds: new Set(),
    lastSelectedWordId: null,
    sectionType: null,
  });

  const { data: userLanguagesData } = useQuery<UserLanguagesResponse>({
    queryKey: ['userLanguages'],
    queryFn: () => MeApi.getUserLanguages(),
  });

  const isEnrolledInLanguagePair = useMemo(() => {
    if (!languagePairId || !userLanguagesData) return null;
    return userLanguagesData.languagePairs?.some(lp => lp.languagePairId === languagePairId);
  }, [languagePairId, userLanguagesData]);

  const {
    data: wordlistResponse,
    isLoading,
    refetch: refetchWordlist,
  } = useQuery({
    queryKey: ['getWordlist', languagePairId, wordlistId],
    queryFn: () => {
      /* istanbul ignore if: should be defined */
      if (!languagePairId || !wordlistId) return null;
      return WordlistsApi.getWordlist(languagePairId, wordlistId);
    },
    enabled: !!languagePairId && !!wordlistId && !emptyPersonalWordlist,
  });
  const {
    data: learnedWordsResponse,
    isLoading: isLoadingLearnedWords,
    fetchNextPage: fetchNextLearnedPage,
    hasNextPage: hasNextLearnedPage,
    isFetchingNextPage: isFetchingNextLearnedPage,
    refetch: refetchLearnedWords,
  } = useInfiniteQuery({
    queryKey: [
      'getWordlistLearnablesWithProgress',
      languagePairId,
      wordlistId,
      'only_with_progress',
    ],
    queryFn: ({ pageParam }) => {
      /* istanbul ignore if: should be defined */
      if (!languagePairId || !wordlistId) return null;
      return WordlistsApi.getWordlistLearnables(languagePairId, wordlistId, {
        wordlist_learnables_state: 'only_with_progress' as LearnablesState,
        learnables_page_token: pageParam || '',
      });
    },
    staleTime: 0,
    gcTime: 0,
    initialPageParam: '',
    getNextPageParam: lastPage =>
      lastPage?.nextLearnablesPageToken ||
      /* istanbul ignore next: please test in WEBSITE-7219 */ undefined,
    enabled: !!languagePairId && !!wordlistId && learnedExpanded && !emptyPersonalWordlist,
  });

  const {
    data: toLearnWordsResponse,
    isLoading: isLoadingToLearnWords,
    fetchNextPage: fetchNextToLearnPage,
    hasNextPage: hasNextToLearnPage,
    isFetchingNextPage: isFetchingNextToLearnPage,
    refetch: refetchToLearnWords,
  } = useInfiniteQuery({
    queryKey: [
      'getWordlistLearnablesWithoutProgress',
      languagePairId,
      wordlistId,
      'only_without_progress',
    ],
    queryFn: ({ pageParam }) => {
      /* istanbul ignore if: should be defined */
      if (!languagePairId || !wordlistId) return null;
      return WordlistsApi.getWordlistLearnables(languagePairId, wordlistId, {
        wordlist_learnables_state: 'only_without_progress' as LearnablesState,
        learnables_page_token: pageParam || '',
      });
    },
    staleTime: 0,
    gcTime: 0,
    initialPageParam: '',
    getNextPageParam: lastPage =>
      lastPage?.nextLearnablesPageToken ||
      /* istanbul ignore next: please test in WEBSITE-7219 */ undefined,
    enabled: !!languagePairId && !!wordlistId && toLearnExpanded && !emptyPersonalWordlist,
  });

  useEffect(() => {
    /* istanbul ignore next: please test in WEBSITE-7219 */
    if (learnedEndOfListInViewport && hasNextLearnedPage && !isFetchingNextLearnedPage) {
      void fetchNextLearnedPage();
    }
    /* istanbul ignore next: please test in WEBSITE-7219 */
    if (toLearnEndOfListInViewport && hasNextToLearnPage && !isFetchingNextToLearnPage) {
      void fetchNextToLearnPage();
    }
  }, [
    learnedEndOfListInViewport,
    toLearnEndOfListInViewport,
    hasNextToLearnPage,
    fetchNextToLearnPage,
    isFetchingNextToLearnPage,
    hasNextLearnedPage,
    isFetchingNextLearnedPage,
    fetchNextLearnedPage,
  ]);

  const learnedWords = useMemo(
    /* istanbul ignore next: optional chaining edge case, pages always defined in normal flow */
    () => learnedWordsResponse?.pages?.flatMap(page => page?.learnables || []) || [],
    [learnedWordsResponse?.pages],
  );

  const toLearnWords = useMemo(
    /* istanbul ignore next: optional chaining edge case, pages always defined in normal flow */
    () => toLearnWordsResponse?.pages?.flatMap(page => page?.learnables || []) || [],
    [toLearnWordsResponse?.pages],
  );
  const wordlist = wordlistResponse?.wordlist;
  const isActiveWordlist = isActive || !!wordlistResponse?.isActive;
  const creator = wordlist?.creator;
  const isActiveInUrlLanguagePair = isActiveWordlist && isDifferentLanguagePair;

  const ctaIcon = useMemo(() => {
    if (wordlist?.isLocked && !isProUser) return 'padlock';
    return undefined;
  }, [wordlist?.isLocked, isProUser]);

  // Blob experience users add community wordlists to their path instead of activating them.
  const canAddToPath =
    !!eligibility?.eligibleForBlobExperience && !isOnboarding && creator === 'user';
  const { skillLevelTrackingData } = useTrackingSkillLevel(canAddToPath ? languagePairId : null);

  const ctaText = useMemo(() => {
    if (wordlist?.isLocked && !isProUser) return msgs.upgradeCta;
    if (isOnboarding) return msgs.chooseCta;
    if (canAddToPath) return msgs.addToPath;
    return msgs.startLesson;
  }, [wordlist?.isLocked, isProUser, isOnboarding, canAddToPath]);

  const wordsToLearn = wordlist?.remainingLearnablesCount;
  const hasNoWords = wordlist?.learnablesCount === 0 || emptyPersonalWordlist;

  const shouldShowViewOnlyModal = () => is2024Experience;

  const shouldShowEnrollmentModal = () => isEnrolledInLanguagePair === false && !!languagePairId;

  const shouldShowSwitchLanguagePairModal = () => {
    if (isActiveInUrlLanguagePair) return true;
    return isEnrolledInLanguagePair === true && isDifferentLanguagePair && !!languagePairId;
  };

  const canActivateWordlist = () => !wordlist?.isLocked || isProUser;

  const handleOnboardingActivation = () => {
    onWordlistActivated?.(wordlistId as string);
    onClose?.();
  };

  const handleRegularActivation = async () => {
    /* istanbul ignore if: defensive check, validated before this function is called */
    if (!languagePairId || !wordlistId) return;

    setIsActive(true);
    await WordlistsApi.activateWordlist(languagePairId, wordlistId);

    void safePush(getAprenderLearnSessionUrl(languagePairId));
  };

  const handleAddToPath = async () => {
    /* istanbul ignore if: defensive check, validated before this function is called */
    if (!languagePairId || !wordlistId) return;

    const trackAddToPath = (result: 'success' | 'error') =>
      void TrackingApi.trackUserAction(
        'clicked_add_to_path',
        'wordlist_details_screen',
        languagePairId,
        !!isProUser,
        {
          ...BLOB_TRACKING_DATA,
          ...skillLevelTrackingData,
          wordlist_id: String(wordlistId),
          result,
        },
      );

    setHasAddToPathError(false);
    try {
      // A page of one blob lands on the current blob, or on the last one when the path is done.
      const { blobs } = await UserPathApi.getBlobs(languagePairId, { limit: 1 });
      await UserPathApi.insertWordlist(languagePairId, wordlistId, blobs[0]?.blobId ?? null);
    } catch (error) {
      // 409: the wordlist is already on the path, which counts as added
      if (!(error instanceof MemriseApiError && error.httpStatusCode === 409)) {
        trackAddToPath('error');
        setHasAddToPathError(true);
        return;
      }
    }

    trackAddToPath('success');
    void safePush(ROUTES.path);
  };

  const handleLockedWordlistRedirect = () => {
    void safePush({
      pathname: isOnboarding ? ROUTES.onboarding.choosePlan : ROUTES.payment.plans,
      query: { next: asPath },
    });
  };

  const handleWordlistSelection = async () => {
    // Blob learners adding to their path get `clicked_add_to_path` once the add settles
    /* istanbul ignore next: analytics */
    if (!canAddToPath) {
      void TrackingApi.trackUserAction(
        'clicked_activate_wordlist_button',
        'wordlist_details_screen',
        languagePairId ?? null,
        !!isProUser,
        { ...(wordlistId && { wordlist_id: String(wordlistId) }) },
      );
    }

    if (shouldShowViewOnlyModal()) {
      setShowViewOnlyModal(true);
      return;
    }

    if (shouldShowEnrollmentModal()) {
      setShowEnrollmentModal(true);
      return;
    }

    if (shouldShowSwitchLanguagePairModal()) {
      setShowSwitchLanguagePairModal(true);
      return;
    }

    if (canActivateWordlist()) {
      if (isOnboarding) {
        handleOnboardingActivation();
      } else if (canAddToPath) {
        await handleAddToPath();
      } else {
        await handleRegularActivation();
      }
    } else {
      handleLockedWordlistRedirect();
    }
  };

  const handleEnrollmentConfirm = async () => {
    /* istanbul ignore if: defensive check, languagePairId validated before modal shows */
    if (!languagePairId) return;

    try {
      await PathApi.enroll(languagePairId);
      /* istanbul ignore if: defensive check, wordlistId validated before modal shows */
      if (!wordlistId) return;
      await WordlistsApi.activateWordlist(languagePairId, wordlistId);

      // Save languagePairId to localStorage before reload
      // Full page reload will fetch fresh user data including language pair details
      saveLastLanguagePair(languagePairId, undefined, undefined, true);

      setShowEnrollmentModal(false);
      /* istanbul ignore next: window.location not testable in JSDOM */
      window.location.href = ROUTES.dashboard;
    } catch (error) {
      setShowEnrollmentModal(false);
    }
  };

  const handleEnrollmentCancel = () => {
    setShowEnrollmentModal(false);
  };

  const handleSwitchLanguagePairConfirm = async () => {
    /* istanbul ignore if: defensive check, validated before modal shows */
    if (!languagePairId || !userLanguagesData) return;

    const targetLanguagePair = userLanguagesData.languagePairs?.find(
      lp => lp.languagePairId === languagePairId,
    );

    /* istanbul ignore if: defensive check, edge case handled in test */
    if (!targetLanguagePair) return;

    try {
      // Only activate if wordlist is not already active in the target LP
      if (!isActiveInUrlLanguagePair && wordlistId) {
        await WordlistsApi.activateWordlist(languagePairId, wordlistId);
      }

      saveLastLanguagePair(
        languagePairId,
        targetLanguagePair.sourceLanguageLocale,
        targetLanguagePair.targetLanguageLocale,
        true,
      );

      setShowSwitchLanguagePairModal(false);
      /* istanbul ignore next: window.location not testable in JSDOM */
      window.location.href = ROUTES.dashboard;
    } catch (error) {
      setShowSwitchLanguagePairModal(false);
    }
  };

  const handleSwitchLanguagePairCancel = () => {
    setShowSwitchLanguagePairModal(false);
  };

  const handleLearnedAccordionToggle = (expanded: boolean) => {
    setLearnedExpanded(expanded);
  };

  const handleToLearnAccordionToggle = (expanded: boolean) => {
    setToLearnExpanded(expanded);
  };

  const shouldExpandLearnedByDefault = wordsToLearn === 0;
  const shouldExpandToLearnByDefault = !!wordsToLearn && wordsToLearn > 0;

  // Create selection actions
  const selectionActions = useMemo(
    () => ({
      toggleSelection: (
        wordId: number,
        isShiftClick = false,
        wordIds: number[] = [],
        sectionType?: 'learned' | 'toLearn',
      ) => {
        setSelectionState(prevState => {
          // Clear selection if switching sections
          if (prevState.sectionType && prevState.sectionType !== sectionType) {
            return {
              selectedWordIds: new Set([wordId]),
              lastSelectedWordId: wordId,
              sectionType: sectionType || null,
            };
          }

          const newSelectedWordIds = new Set(prevState.selectedWordIds);

          if (isShiftClick && prevState.lastSelectedWordId !== null && wordIds.length > 0) {
            // Range selection with shift click
            const lastIndex = wordIds.indexOf(prevState.lastSelectedWordId);
            const currentIndex = wordIds.indexOf(wordId);

            if (lastIndex !== -1 && currentIndex !== -1) {
              const start = Math.min(lastIndex, currentIndex);
              const end = Math.max(lastIndex, currentIndex);

              for (let i = start; i <= end; i++) {
                newSelectedWordIds.add(wordIds[i]);
              }
            }

            return {
              selectedWordIds: newSelectedWordIds,
              lastSelectedWordId: wordId,
              sectionType: sectionType || prevState.sectionType,
            };
          } else {
            // Regular toggle selection
            if (newSelectedWordIds.has(wordId)) {
              newSelectedWordIds.delete(wordId);
            } else {
              newSelectedWordIds.add(wordId);
            }

            return {
              selectedWordIds: newSelectedWordIds,
              lastSelectedWordId: wordId,
              sectionType: sectionType || prevState.sectionType,
            };
          }
        });
      },

      clearSelection: () => {
        setSelectionState({
          selectedWordIds: new Set(),
          lastSelectedWordId: null,
          sectionType: null,
        });
      },

      selectRange: (
        startId: number,
        endId: number,
        wordIds: number[],
        sectionType?: 'learned' | 'toLearn',
      ) => {
        setSelectionState(prevState => {
          const newSelectedWordIds = new Set(prevState.selectedWordIds);
          const startIndex = wordIds.indexOf(startId);
          const endIndex = wordIds.indexOf(endId);

          if (startIndex !== -1 && endIndex !== -1) {
            const start = Math.min(startIndex, endIndex);
            const end = Math.max(startIndex, endIndex);

            for (let i = start; i <= end; i++) {
              newSelectedWordIds.add(wordIds[i]);
            }
          }

          return {
            ...prevState,
            selectedWordIds: newSelectedWordIds,
            sectionType: sectionType || prevState.sectionType,
          };
        });
      },
    }),
    [],
  );

  const handleBulkMarkAsKnown = async () => {
    if (!languagePairId || selectionState.selectedWordIds.size === 0) return;

    try {
      await bulkMarkAsKnown({
        languagePairId,
        wordIds: Array.from(selectionState.selectedWordIds),
        onSuccess: () => {
          void refetchLearnedWords();
          void refetchToLearnWords();
          void refetchWordlist();
          selectionActions.clearSelection();
        },
      });
    } catch (error) {
      console.error('Bulk mark as known failed:', error);
    }
  };

  const handleBulkUnmarkAsKnown = async () => {
    if (!languagePairId || selectionState.selectedWordIds.size === 0) return;

    try {
      await bulkUnmarkAsKnown({
        languagePairId,
        wordIds: Array.from(selectionState.selectedWordIds),
        onSuccess: () => {
          void refetchLearnedWords();
          void refetchToLearnWords();
          void refetchWordlist();
          selectionActions.clearSelection();
        },
      });
    } catch (error) {
      console.error('Bulk unmark as known failed:', error);
    }
  };

  const handleBulkCancel = () => {
    selectionActions.clearSelection();
  };

  if (hasNoWords) {
    return (
      <S.Container $hasWhiteBackground>
        <BackHeader href={resolvedBackRoute} />
        <S.Content>
          <ImageBanner
            title={intl.formatMessage(msgs.placeholderTitle)}
            pictureUrl="https://static.memrise.com/uploads/Words%20Added%20by%20Me/Words_added_by_me_(Web).png"
            isActive={false}
            isPremium={false}
            isLocked={false}
            isCompleted={false}
            wordsToLearn={0}
            creator="user"
            creatorId={wordlist?.creatorId}
            wordlistId={wordlistId}
            languagePairId={languagePairId}
          />
          <EmptyWordlistCard handleWordlistSelection={handleWordlistSelection} />
        </S.Content>
      </S.Container>
    );
  }

  if (isLoading) {
    return (
      <S.LoaderContainer>
        <Loader />
      </S.LoaderContainer>
    );
  }

  return (
    <S.Container>
      {!isOnboarding && <BackHeader href={resolvedBackRoute} />}
      {wordlist && (
        <S.Content>
          <ImageBanner
            title={wordlist.title}
            pictureUrl={wordlist.pictureUrl}
            isActive={isActiveWordlist && !isOnboarding}
            isPremium={wordlist.isPremium}
            isLocked={wordlist.isLocked}
            isCompleted={wordlist.isCompleted}
            wordsToLearn={wordlist.remainingLearnablesCount}
            creator={wordlist.creator}
            creatorId={wordlist.creatorId}
            wordlistId={wordlistId}
            languagePairId={languagePairId}
            description={wordlist.description}
            creatorUsername={wordlist.creatorUsername}
            userCollectionCount={wordlist.userCollectionCount}
          />
          <WordsAccordion
            bgColor="neutral98"
            title={intl.formatMessage(msgs.learnedWords)}
            wordsCount={wordlist.learnedLearnablesCount}
            words={learnedWords}
            expanded={shouldExpandLearnedByDefault}
            showOptions={!isOnboarding && (isProUser || !wordlist.isLocked)}
            creator={creator}
            creatorId={wordlist.creatorId}
            onToggleExpand={handleLearnedAccordionToggle}
            showNextReview={!!eligibility?.eligibleForBlobExperience}
            refetchLearnables={() => {
              void refetchLearnedWords();
              void refetchToLearnWords();
              void refetchWordlist();
            }}
            sectionType="learned"
            selectionState={selectionState}
            selectionActions={selectionActions}
            wordlistId={wordlistId}
          />
          {learnedExpanded && learnedWords.length > 0 && <div ref={learnedEndOfListRef} />}
          {learnedExpanded && (isLoadingLearnedWords || isFetchingNextLearnedPage) && (
            /* istanbul ignore next: please test in WEBSITE-7219 */
            <S.LoaderContainer>
              <Loader />
            </S.LoaderContainer>
          )}
          <div ref={accordionMiddleRef} />
          <WordsAccordion
            title={intl.formatMessage(msgs.wordsToLearn)}
            wordsCount={wordlist.remainingLearnablesCount}
            words={toLearnWords}
            expanded={shouldExpandToLearnByDefault}
            showOptions={!isOnboarding && (isProUser || !wordlist.isLocked)}
            creator={creator}
            creatorId={wordlist.creatorId}
            onToggleExpand={handleToLearnAccordionToggle}
            hideDifficultToggle
            refetchLearnables={() => {
              void refetchLearnedWords();
              void refetchToLearnWords();
              void refetchWordlist();
            }}
            sectionType="toLearn"
            selectionState={selectionState}
            selectionActions={selectionActions}
            wordlistId={wordlistId}
          />
          {toLearnExpanded && toLearnWords.length > 0 && <div ref={toLearnEndOfListRef} />}
          {toLearnExpanded && (isLoadingToLearnWords || isFetchingNextToLearnPage) && (
            /* istanbul ignore next: please test in WEBSITE-7219 */
            <S.LoaderContainer>
              <Loader />
            </S.LoaderContainer>
          )}
        </S.Content>
      )}
      {!inViewportFilter && <BackToTopArrow />}
      {!hasNoWords && (!is2024Experience || isActiveInUrlLanguagePair) && (
        <S.StickyWrapper>
          <Button
            icon={ctaIcon}
            data-testid="wordlist_switch_btn"
            buttonSize="large"
            buttonType="primary"
            wide
            onClick={handleWordlistSelection}
            error={hasAddToPathError ? intl.formatMessage(msgs.addToPathError) : undefined}
          >
            {intl.formatMessage(ctaText)}
          </Button>
        </S.StickyWrapper>
      )}
      <ActionsPopup
        header={intl.formatMessage(msgs.enrollmentHeader)}
        text={intl.formatMessage(msgs.enrollmentBody)}
        primaryBtnText={intl.formatMessage(msgs.enrollmentConfirm)}
        primaryBtnAction={handleEnrollmentConfirm}
        secondaryBtnText={intl.formatMessage(msgs.enrollmentCancel)}
        secondaryBtnAction={handleEnrollmentCancel}
        isOpen={showEnrollmentModal}
        onClose={handleEnrollmentCancel}
      />
      <ActionsPopup
        header={intl.formatMessage(msgs.viewOnlyHeader)}
        text={intl.formatMessage(msgs.viewOnlyBody)}
        primaryBtnText={intl.formatMessage(msgs.viewOnlyClose)}
        primaryBtnAction={() => setShowViewOnlyModal(false)}
        isOpen={showViewOnlyModal}
        onClose={() => setShowViewOnlyModal(false)}
      />
      <ActionsPopup
        header={intl.formatMessage(msgs.switchLanguagePairHeader)}
        text={intl.formatMessage(msgs.switchLanguagePairBody)}
        primaryBtnText={intl.formatMessage(msgs.switchLanguagePairConfirm)}
        primaryBtnAction={handleSwitchLanguagePairConfirm}
        secondaryBtnText={intl.formatMessage(msgs.switchLanguagePairCancel)}
        secondaryBtnAction={handleSwitchLanguagePairCancel}
        isOpen={showSwitchLanguagePairModal}
        onClose={handleSwitchLanguagePairCancel}
      />
      {selectionState.selectedWordIds.size > 0 && selectionState.sectionType && (
        <BulkActionsBar
          selectedCount={selectionState.selectedWordIds.size}
          sectionType={selectionState.sectionType}
          onMarkAsKnown={handleBulkMarkAsKnown}
          onUnmarkAsKnown={handleBulkUnmarkAsKnown}
          onCancel={handleBulkCancel}
        />
      )}
    </S.Container>
  );
};

export const WordlistDetailsPage: FC = () => {
  const intl = useIntl();

  return (
    <DashboardFrame
      hasSidebar={false}
      pageScreenName={PlatformPage.WORDLISTS}
      pageTitle={intl.formatMessage(msgs.title)}
    >
      <WordlistsDetailsPageComponent />
    </DashboardFrame>
  );
};

export default WordlistDetailsPage;
